import styled, { keyframes } from "styled-components";

/* ═══════════════════════════════════════════════════════════════════════
   UsernameModal — Light Water redesign
   Frosted-white shell with gradient title, frosted close circle, and a
   light water input pill. JS layer untouched.
   ═══════════════════════════════════════════════════════════════════════ */

const panelFloat = keyframes`
  0%, 100% { filter: brightness(1); }
  50%      { filter: brightness(1.02); }
`;

export const ModalContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 360px;
  border-radius: 28px;
  border: 1px solid var(--glass-edge);
  background:
    radial-gradient(
      120% 60% at 30% -10%,
      rgba(var(--glass-white-rgb), 0.96) 0%,
      rgba(var(--glass-pale-rgb), 0.9) 55%,
      rgba(var(--glass-pale-rgb), 0.82) 100%
    ),
    linear-gradient(
      180deg,
      rgba(var(--glass-white-rgb), 0.94) 0%,
      rgba(var(--glass-pale-rgb), 0.9) 100%
    );
  backdrop-filter: blur(28px) saturate(180%);
  -webkit-backdrop-filter: blur(28px) saturate(180%);
  box-shadow:
    0 32px 64px -18px rgba(var(--color-brand-500-rgb), 0.4),
    0 12px 28px rgba(var(--color-brand-400-rgb), 0.22),
    inset 0 2px 0 rgba(255, 255, 255, 0.95),
    inset 0 -10px 22px rgba(var(--color-brand-200-rgb), 0.26),
    inset 0 0 0 1px rgba(255, 255, 255, 0.45);
  isolation: isolate;
  animation: ${panelFloat} 5.5s ease-in-out infinite;

  [data-theme="dark"] & {
    box-shadow:
      0 28px 56px -18px rgba(0, 0, 0, 0.6),
      0 10px 24px rgba(0, 0, 0, 0.4),
      inset 0 -10px 22px rgba(var(--color-brand-700-rgb), 0.18);
    &::after {
      opacity: 0;
    }
  }

  /* Top specular sheen */
  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 32px;
    right: 32px;
    height: 20px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.9) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(2px);
    pointer-events: none;
    z-index: 10;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  /* Bottom aqua underglow */
  &::after {
    content: "";
    position: absolute;
    bottom: -8px;
    left: 25%;
    right: 25%;
    height: 16px;
    background: radial-gradient(
      ellipse at center top,
      rgba(var(--color-brand-400-rgb), 0.42) 0%,
      rgba(var(--color-brand-400-rgb), 0) 70%
    );
    pointer-events: none;
    z-index: -1;
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

/* Frosted-glass circle close button */
export const CloseButton = styled.button`
  position: relative;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid var(--glass-edge-soft);
  background: radial-gradient(
    130% 100% at 30% 15%,
    rgba(var(--glass-white-rgb), 0.78) 0%,
    rgba(var(--glass-white-rgb), 0.5) 45%,
    rgba(var(--glass-pale-rgb), 0.38) 100%
  );
  backdrop-filter: blur(18px) saturate(180%);
  -webkit-backdrop-filter: blur(18px) saturate(180%);
  box-shadow:
    0 6px 14px rgba(var(--color-brand-500-rgb), 0.14),
    inset 0 1px 0 rgba(255, 255, 255, 0.95),
    inset 0 -3px 8px rgba(var(--color-brand-200-rgb), 0.22);
  cursor: pointer;
  outline: none;
  transition:
    transform 200ms var(--ease-snappy),
    border-color 200ms var(--ease-snappy);

  [data-theme="dark"] & {
    background: var(--color-surface-3);
    box-shadow:
      0 4px 10px rgba(0, 0, 0, 0.45),
      inset 0 -2px 6px rgba(var(--color-brand-700-rgb), 0.16);
  }

  /* Top-left specular */
  &::before {
    content: "";
    position: absolute;
    top: 3px;
    left: 5px;
    width: 16px;
    height: 9px;
    border-radius: 50%;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.85) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  svg {
    width: 14px;
    height: 14px;
    position: relative;
    z-index: 1;

    path {
      stroke: var(--color-brand-700);
    }
  }

  [data-theme="dark"] & svg path {
    stroke: var(--color-text-strong);
  }

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(var(--color-brand-400-rgb), 0.55);
  }
`;

export const ModalContent = styled.div`
  position: relative;
  z-index: 1;
  padding: 24px 22px 22px;
  display: flex;
  flex-direction: column;
  gap: 18px;

  @media (max-width: 480px) {
    padding: 20px 18px 18px;
  }
`;

export const TitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
`;

export const ModalTitle = styled.h2`
  margin: 0;
  font-family: var(--font-family-primary);
  font-size: 22px;
  font-weight: 800;
  line-height: normal;
  letter-spacing: -0.03em;

  /* Brand gradient title */
  background: linear-gradient(
    0deg,
    var(--color-text-strong) 0%,
    var(--color-text-body) 45%,
    var(--color-brand-600) 100%
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  filter: drop-shadow(0 1px 2px rgba(var(--color-brand-600-rgb), 0.14));
`;

export const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

export const Label = styled.span`
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const Input = styled.input`
  width: 100%;
  height: 48px;
  padding: 0 18px;
  box-sizing: border-box;
  border-radius: 999px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-input);
  backdrop-filter: blur(18px) saturate(160%);
  -webkit-backdrop-filter: blur(18px) saturate(160%);
  box-shadow: var(--shadow-micro);
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.005em;
  outline: none;
  transition:
    border-color 220ms var(--ease-snappy),
    box-shadow 220ms var(--ease-snappy);

  &::placeholder {
    color: var(--color-text-dim);
    font-weight: 600;
  }

  &:focus {
    border-color: var(--color-line-strong);
    box-shadow: var(--shadow-micro), var(--shadow-glow);
  }
`;
